import { useEffect, useRef, useState } from 'preact/hooks';

interface YearRangeSliderProps {
  years: string[];
  start: string;
  end: string;
  onChange: (range: { start: string; end: string }) => void;
  className?: string;
  disabled?: boolean;
}

interface RangeIndices {
  start: number;
  end: number;
}

function clampIndex(index: number, min: number, max: number): number {
  return Math.min(Math.max(index, min), max);
}

export function YearRangeSlider({
  years,
  start,
  end,
  onChange,
  className = '',
  disabled = false,
}: YearRangeSliderProps) {
  const minIndex = 0;
  const maxIndex = Math.max(years.length - 1, 0);

  const selectedStartIndex = clampIndex(
    years.indexOf(start) === -1 ? minIndex : years.indexOf(start),
    minIndex,
    maxIndex,
  );
  const selectedEndIndex = clampIndex(
    years.indexOf(end) === -1 ? maxIndex : years.indexOf(end),
    selectedStartIndex,
    maxIndex,
  );

  // While a thumb is being dragged the range lives here, so the parent is not
  // asked to refetch on every intermediate step.
  const [draftRange, setDraftRange] = useState<RangeIndices | null>(null);

  useEffect(() => {
    setDraftRange(null);
  }, [start, end, years.length]);

  const startIndex = draftRange ? draftRange.start : selectedStartIndex;
  const endIndex = draftRange ? draftRange.end : selectedEndIndex;

  const commitDraftRange = () => {
    if (!draftRange) return;
    const nextStart = years[draftRange.start];
    const nextEnd = years[draftRange.end];
    if (nextStart === start && nextEnd === end) {
      setDraftRange(null);
      return;
    }
    onChange({ start: nextStart, end: nextEnd });
  };

  const commitDraftRangeRef = useRef(commitDraftRange);
  commitDraftRangeRef.current = commitDraftRange;

  // A thumb can be released outside the input, so the release is watched globally.
  const isDragging = draftRange !== null;
  useEffect(() => {
    if (!isDragging) return undefined;
    const handleRelease = () => commitDraftRangeRef.current();
    window.addEventListener('pointerup', handleRelease);
    window.addEventListener('pointercancel', handleRelease);
    return () => {
      window.removeEventListener('pointerup', handleRelease);
      window.removeEventListener('pointercancel', handleRelease);
    };
  }, [isDragging]);

  if (years.length === 0) {
    return (
      <div className={`year-range-slider year-range-slider--empty ${className}`.trim()}>
        <span className="year-range-slider__label">No period data</span>
      </div>
    );
  }

  const span = Math.max(maxIndex - minIndex, 1);
  const leftPct = ((startIndex - minIndex) / span) * 100;
  const rightPct = ((endIndex - minIndex) / span) * 100;

  const draftStart = (index: number) => {
    setDraftRange({ start: clampIndex(index, minIndex, endIndex), end: endIndex });
  };

  const draftEnd = (index: number) => {
    setDraftRange({ start: startIndex, end: clampIndex(index, startIndex, maxIndex) });
  };

  return (
    <div className={`year-range-slider ${disabled ? 'is-disabled' : ''} ${className}`.trim()}>
      <span className="year-range-slider__bound year-range-slider__bound--start">
        {years[startIndex]}
      </span>
      <div className="year-range-slider__track-wrap">
        <div className="year-range-slider__track" aria-hidden="true">
          <div
            className="year-range-slider__fill"
            style={{ left: `${leftPct}%`, width: `${Math.max(rightPct - leftPct, 0)}%` }}
          />
        </div>
        <input
          type="range"
          className="year-range-slider__input year-range-slider__input--start"
          min={minIndex}
          max={maxIndex}
          step={1}
          value={startIndex}
          disabled={disabled || years.length < 2}
          aria-label="Range start year"
          onInput={(event) => draftStart(Number((event.currentTarget as HTMLInputElement).value))}
          onKeyUp={commitDraftRange}
          onBlur={commitDraftRange}
        />
        <input
          type="range"
          className="year-range-slider__input year-range-slider__input--end"
          min={minIndex}
          max={maxIndex}
          step={1}
          value={endIndex}
          disabled={disabled || years.length < 2}
          aria-label="Range end year"
          onInput={(event) => draftEnd(Number((event.currentTarget as HTMLInputElement).value))}
          onKeyUp={commitDraftRange}
          onBlur={commitDraftRange}
        />
      </div>
      <span className="year-range-slider__bound year-range-slider__bound--end">
        {years[endIndex]}
      </span>
    </div>
  );
}
